import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonWidth,
  Drawer,
  DropDown,
  Label,
  MLPVersion,
  TableWithSearchTemplate,
} from '../../../ui/components';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { useAPICall } from '../../../hooks';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import { DynamicObjectInterface } from '../../../sdk/type';
import LeaderService from '../../../sdk/services/leaderService';
import CampaignService from '../../../sdk/services/campaignService';
import { LeaderData, MasterData, CampaignOption, dataSearchOption, getLeaderListConfig } from './leaderConfig';
import styles from './index.module.scss';

export const AdminGeneralLeadersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();

  const [data, setData] = useState<LeaderData[]>([]);
  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [selectedRow, setSelectedRow] = useState<LeaderData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [displayEmpty, setDisplayEmpty] = useState(false);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCoachListByCampaignId();
    }
  }, [selectedCampaign]);

  const fetchCoachListByCampaignId = async () => {
    const result = await apiCall(LeaderService.getLeaderByCampaignId, selectedCampaign?.value!);
    if (result.status === 200) {
      const res = result?.data?.data?.filter((i: LeaderData) => i.totalCoach > 0);
      setDisplayEmpty(res?.length === 0);
      setData(res ?? []);
    } else {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve leader list by campaign');
    }
  };

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'CHALLENGE'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve coach list', error);
    }
  };

  const onEdit = (rec: LeaderData | undefined) => {
    let recordId = rec?.recId ?? selectedRow?.recId;
    navigate(`/admin/manage-leader/edit?=${recordId}&campaignId=${selectedCampaign?.campaignId}`);
  };

  const handleInputChange = (value: DynamicObjectInterface) => {
    navigate(`?campaignId=${value.campaignId}`);
  };

  const drawerDetails = (
    <>
      {drawerOpen && (
        <Drawer
          onClose={() => setDrawerOpen(false)}
          closeOnBackdropClick={true}
          closeIcon={true}
          header={<div>Details</div>}
          drawerWidth={'490px'}
          bottomGapFooter="0px"
          footer={
            <div>
              <Button
                title="Edit"
                onClick={() => onEdit(undefined)}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          }
        >
          <div className={styles['summary-pane-body']}>
            <div className={styles['basic-details']}>
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalLeader.campaignId')} value={selectedRow?.campaignId} />
                  <Label title={t('admin.generalLeader.campaignName')} value={selectedRow?.campaignName} />
                </div>
              </div>
              <hr className={styles['horizontal-divider']} />
              <div className={styles['title-value']}>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalLeader.leaderAboId')} value={selectedRow?.aboId} />
                  <Label title={t('admin.generalLeader.leaderName')} value={selectedRow?.name} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalLeader.totalCoach')} value={`${selectedRow?.totalCoach ?? 0}`} />
                  <Label
                    title={t('admin.generalLeader.totalQualifiedCoach')}
                    value={`${
                      Number(selectedRow?.totalQualifiedCoach.value ?? 0) +
                      Number(selectedRow?.totalQualifiedCoach.valueAdj ?? 0)
                    }`}
                  />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalLeader.phone')} value={selectedRow?.phone} />
                  <Label title={t('admin.generalLeader.email')} value={selectedRow?.email} />
                </div>
                <div className={styles['two-columns']}>
                  <Label title={t('admin.generalLeader.leaderPin')} value={selectedRow?.pin} />
                </div>
              </div>
              <hr className={styles['horizontal-divider']} />
              <div className={styles['title-value']}>
                <div className={styles['rewards-table']}>
                  <Label
                    title={t('admin.generalLeader.rewards')}
                    value={selectedRow?.rewards?.length ? '' : t('admin.generalLeader.noRewards')}
                  />
                  {selectedRow?.rewards && (
                    <table>
                      <tbody>
                        {selectedRow?.rewards?.map(reward => (
                          <>
                            <tr>
                              <td className={styles['rewards-table-label']}>Code</td>
                              <td className={styles['rewards-table-value']}>{reward.rewardCode ?? '-'}</td>
                            </tr>
                            <tr>
                              <td className={styles['rewards-table-label']}>Name</td>
                              <td className={styles['rewards-table-value']}>{reward.rewardName ?? '-'}</td>
                            </tr>
                            <tr>
                              <td className={styles['rewards-table-label']}>Redeem Date</td>
                              <td className={styles['rewards-table-value']}>{reward.redeemDate ?? '-'}</td>
                            </tr>
                            <tr>
                              <td className={styles['rewards-table-label']}>Remarks</td>
                              <td className={styles['rewards-table-value']}>{reward.rewardRemarks ?? '-'}</td>
                            </tr>
                          </>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.generalLeader.leaderList')} />
      <div className={styles['leader-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['leader-list-top-layout']}>
          <div className={styles['leader-list-top-info']}>
            <div>{t('admin.generalLeader.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['leader-list-top-info']}>
            <div>{t('admin.generalLeader.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          searchByStrip
          getColumns={getLeaderListConfig}
          data={dataSearchOption}
          hideAddButton
          rowKey="recId"
          selected={`${selectedRow?.recId}`}
          cb={{ onEdit }}
          onRowClick={row => {
            setSelectedRow(row);
            setDrawerOpen(true);
          }}
        />
      )}
      {drawerDetails}
    </div>
  );
};
